import { useTheme } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import {
  CurrencyType,
  TransactionStatusType,
  TransactionType,
} from '@libs/types';
import {
  Button,
  LongArrowIcon,
  STATUS_LABELS,
  Typography,
  useStatusInfo,
} from '@libs/components';
import { screenSizes } from '@libs/theme';

import { getRecentTransactionsQuery } from 'src/gql';
import {
  useApiResponse,
  useBalanceVisibility,
  useDimensions,
  useTransactionsModal,
  useTranslation,
} from 'src/hooks';
import { TRANSACTION_LABELS } from 'src/constants';

import { COIN_DETAILS_SECTION_IDS } from 'src/pages/coin-details/constants/constants';
import { getAlternativeAssetLabel } from 'src/helpers';
import { DesertedIcon } from 'src/assets';
import { DashboardTransactionsLoader } from 'src/content-loaders';
import { DataWithLoader } from 'src/components';

import {
  Body,
  Container,
  CustomAssetNetworkIcon,
  FlexCenter,
  Header,
  IconWrapper,
  ImageTitleContainer,
  ListContainer,
  CodeTypography,
  Separator,
  TitleContainer,
  TextSeparator,
  ArrowWrapper,
  Footer,
  DescrContainer,
  ListWrapper,
  GrayBullets,
  CustomBullets,
} from './styles';

type DashboardTransactionGeProps = {
  assetCode?: CurrencyType;
};
export const DashboardTransactionGe: React.FC<DashboardTransactionGeProps> = ({
  assetCode,
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { width } = useDimensions();
  const navigate = useNavigate();
  const { isBalanceHidden } = useBalanceVisibility();

  const { transactionModal, openModal, handleTransactionDetails } =
    useTransactionsModal(0);
  const handleTransactionClick = (selectedId: number) => {
    handleTransactionDetails(selectedId);
    openModal();
  };

  const { getStatusData } = useStatusInfo();
  const resp = useQuery(getRecentTransactionsQuery, {
    variables: {
      record: {
        skip: 0,
        take: 5,
        assetCodes: assetCode ? [assetCode] : [],
        types: [],
        statuses: [],
      },
    },
    fetchPolicy: 'network-only',
  });
  const { response, loading } = useApiResponse(resp);

  const handleSeeAll = () => {
    if (assetCode) {
      navigate(`/transactions?assetCode=${assetCode}`);
    } else {
      navigate('/transactions');
    }
  };

  const amountTextType =
    width < screenSizes.mobileM ? 'bodyButton' : 'subHeadline';

  const title = assetCode
    ? t('transactions_with_asset_code_title', {
        assetCode: getAlternativeAssetLabel(assetCode),
      })
    : t('dashboard_my_transactions');

  return (
    <Container id={COIN_DETAILS_SECTION_IDS.TRANSACTIONS}>
      <Header>
        <Typography type="subHeadline" fontFamily="primaryBold">
          {title}
        </Typography>
      </Header>
      <DataWithLoader
        hasData={!!response?.getRecentTransactions.length}
        loader={<DashboardTransactionsLoader />}
        loading={loading}
        emptyStateTitle={t('dashboard_transaction_not_found_title')}
        icon={DesertedIcon}
      >
        <>
          <Body>
            {response?.getRecentTransactions.map((resp) => {
              const details = resp.details;
              const transactionStatus =
                resp.status === TransactionStatusType.DRAFT
                  ? getStatusData(TransactionStatusType.PENDING)
                  : getStatusData(resp.status);

              const isTypeConvert =
                details.type === TransactionType.Buy ||
                details.type === TransactionType.Sell;

              const assetSubCode =
                CurrencyType.GEL === details.assetCode ||
                CurrencyType.USD === details.assetCode
                  ? ''
                  : details.assetCode;

              return (
                <ListWrapper key={resp.id}>
                  <ListContainer
                    onClick={() => handleTransactionClick(resp.id)}
                  >
                    <ImageTitleContainer>
                      <IconWrapper>
                        <CustomAssetNetworkIcon
                          isAlone={!details.quoteAssetIconUrl}
                          baseUrl={details.assetIconUrl}
                          networkIcon={details.quoteAssetIconUrl}
                        />
                      </IconWrapper>
                      <TitleContainer>
                        <FlexCenter>
                          <Typography
                            type="subHeadline"
                            fontFamily="primaryMedium"
                          >
                            {details.fromAsset?.name
                              ? details.fromAsset.name
                              : details.assetLabel}
                          </Typography>
                          {!details.toAsset?.name && (
                            <CodeTypography
                              type="bodyButton"
                              fontFamily="primaryRegular"
                              color={colors.text.secondary}
                            >
                              {assetSubCode}
                            </CodeTypography>
                          )}
                          {details.toAsset?.name && (
                            <FlexCenter>
                              <ArrowWrapper>
                                <LongArrowIcon />
                              </ArrowWrapper>
                              <Typography
                                type="subHeadline"
                                fontFamily="primaryMedium"
                              >
                                {details.toAsset?.name}
                              </Typography>
                            </FlexCenter>
                          )}
                        </FlexCenter>
                        <FlexCenter>
                          <Typography
                            type="caption"
                            fontFamily="primaryMedium"
                            color={colors.text.secondary}
                          >
                            {t(TRANSACTION_LABELS[resp.details.type])}
                          </Typography>
                          <TextSeparator>/</TextSeparator>
                          <Typography
                            type="caption"
                            fontFamily="primaryMedium"
                            color={transactionStatus.colors[0]}
                          >
                            {t(STATUS_LABELS[resp.status])}
                          </Typography>
                        </FlexCenter>
                      </TitleContainer>
                    </ImageTitleContainer>
                    <DescrContainer>
                      {isBalanceHidden ? (
                        <CustomBullets bulletAmount={3} />
                      ) : (
                        <Typography
                          type={amountTextType}
                          fontFamily="primaryBold"
                        >
                          {isTypeConvert ? details?.toAmount : details.amount}{' '}
                          {isTypeConvert
                            ? getAlternativeAssetLabel(
                                details.toAsset?.code as CurrencyType
                              )
                            : details.assetCode}
                        </Typography>
                      )}

                      {isTypeConvert && (
                        <Typography type="caption" fontFamily="primaryMedium">
                          {isBalanceHidden ? (
                            <GrayBullets bulletAmount={6} />
                          ) : (
                            ` -${details?.fromAmount} ${details.fromAsset?.name}`
                          )}
                        </Typography>
                      )}
                    </DescrContainer>
                  </ListContainer>
                  <Separator className="separator" />
                </ListWrapper>
              );
            })}
          </Body>
          <Footer>
            <Button
              buttonType="secondary"
              size="sm"
              title={t('notifications_view_all')}
              onClick={handleSeeAll}
            />
          </Footer>
        </>
      </DataWithLoader>
      {transactionModal}
    </Container>
  );
};
export default DashboardTransactionGe;
